import { Button, notify, Progress } from '@eleveo/components-react';
import { CheckmarkIcon } from '@eleveo/icons-react';
import React, { useCallback, useEffect } from 'react';
import { useField, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { useConfig } from '../../../app/ConfigurationProvider';
import { useIntegrations } from '../../../app/IntegrationsProvider';
import { TEST_IDS } from '../../../constants/dataTestIds';
import { CacheTokensParams, LOGIN_STATE, WEBEX_FAIL_REASON } from '../../../hooks/webex/types';
import { useWebexAuthentication } from '../../../hooks/webex/useWebexAuthentication';
import { INTEGRATION_FORM_FIELDS, TEMPORARY_FORM_FIELDS } from '../../../scene/integrations/types';
import { required } from '../../../utils/validations/required';
import { ETL_CONFIGURATION_STATUS, ETL_INTEGRATION_TYPE, Values } from '../../configuration/types';

type WebexAuthButtonProps = Omit<React.ComponentProps<typeof Button>, 'children'>;

export const WebexAuthButton: React.FC<WebexAuthButtonProps> = ({ ...buttonProps }) => {
  const { t } = useTranslation();

  const { isInReadOnlyMode, isInEditMode, integrationType } = useIntegrations();

  const {
    appUrl,
    webexParamsUrl: paramsUrl,
    webexTokensUrl: tokensUrl,
    webexTimeout: timeoutDuration,
    webexCCScopes,
    webexCallingScopes,
  } = useConfig();

  const scopes = (() => {
    switch (integrationType) {
      case ETL_INTEGRATION_TYPE.wxcc:
        return webexCCScopes;
      case ETL_INTEGRATION_TYPE.wxcalling:
        return webexCallingScopes;
      default:
        throw new Error(`Invalid integration type! "${integrationType}"`);
    }
  })();

  const { change, batch } = useForm();

  const {
    input: { value: clientId },
    meta: { invalid: isIdInvalid, pristine: isIdPristine, initial: clientIdInitial },
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  } = useField(INTEGRATION_FORM_FIELDS.id(integrationType));

  const {
    input: { value: clientSecret },
    meta: { invalid: isSecretInvalid, pristine: isSecretPristine, initial: clientSecretInitial },
  } = useField(INTEGRATION_FORM_FIELDS.secret(integrationType));

  const onSuccess = useCallback(() => {
    notify.success(t('webex.login.success'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFailed = useCallback((cause: Values<typeof WEBEX_FAIL_REASON>) => {
    notify.error(t(`webex.login.failed.${String(cause).toLowerCase()}`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTimeout = useCallback(() => {
    notify.warning(t(`webex.login.timeout`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    loginState,
    triggerWebexLogin,
    webexTokens,
    resetAuthentication,
    wasPreviouslyLoggedIn,
    restorePreviousLogin,
  } = useWebexAuthentication({
    onSuccess,
    onFailed,
    handleTimeout,
    appUrl,
    paramsUrl,
    tokensUrl,
    timeoutDuration,
  });

  const {
    input: { value: accessToken },
  } = useField(INTEGRATION_FORM_FIELDS.accessToken(integrationType), { validate: required });

  const {
    input: { value: configurationState },
    meta: { pristine: isConfigurationStatusPristine },
  } = useField(INTEGRATION_FORM_FIELDS.state);

  const {
    input: { value: accessTokenExpiresIn },
  } = useField(INTEGRATION_FORM_FIELDS.accessTokenExpires(integrationType), { validate: required });

  const {
    input: { value: refreshToken },
  } = useField(INTEGRATION_FORM_FIELDS.refreshToken(integrationType), { validate: required });
  const {
    input: { value: refreshTokenExpires },
  } = useField(INTEGRATION_FORM_FIELDS.refreshTokenExpires(integrationType), { validate: required });

  const resetAuthenticationInternal = (shouldCacheTokens: boolean) => {
    let cacheTokenParams: CacheTokensParams | undefined;

    if (shouldCacheTokens && clientIdInitial && clientSecretInitial) {
      cacheTokenParams = {
        clientId: clientIdInitial,
        clientSecret: clientSecretInitial,
        scope: scopes,
        webexTokens: {
          refresh_token: refreshToken,
          refresh_token_expires_in: refreshTokenExpires,
          access_token: accessToken,
          expires_in: accessTokenExpiresIn,
          token_type: 'Bearer',
          startTime: undefined,
          state: configurationState,
        },
      };
    }

    batch(() => {
      // also remove time when authorization was successful
      change(TEMPORARY_FORM_FIELDS.startTime, undefined);
      change(INTEGRATION_FORM_FIELDS.accessToken(integrationType), undefined);
      change(INTEGRATION_FORM_FIELDS.accessTokenExpires(integrationType), undefined);
      change(INTEGRATION_FORM_FIELDS.refreshToken(integrationType), undefined);
      change(INTEGRATION_FORM_FIELDS.refreshTokenExpires(integrationType), undefined);
      change(INTEGRATION_FORM_FIELDS.state, ETL_CONFIGURATION_STATUS.unauthorized);
    });

    resetAuthentication(cacheTokenParams);
  };

  useEffect(() => {
    if (!isConfigurationStatusPristine) {
      return;
    }

    if (configurationState !== ETL_CONFIGURATION_STATUS.unauthorized) {
      return;
    }

    notify.warning(t('myConfigurations.reauthorizationNeeded'));
    resetAuthenticationInternal(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurationState]);

  useEffect(() => {
    const previouslyLoggedIn = wasPreviouslyLoggedIn(clientId, clientSecret, scopes);

    if (isSecretPristine && isIdPristine) {
      if (previouslyLoggedIn) {
        restorePreviousLogin(clientId, clientSecret, scopes);
      }
      return;
    }

    if (!webexTokens && !isInEditMode && !previouslyLoggedIn) {
      return;
    }

    if (previouslyLoggedIn) {
      restorePreviousLogin(clientId, clientSecret, scopes);
      return;
    }

    resetAuthenticationInternal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, clientSecret]);

  useEffect(() => {
    /**
     * dont clear previously stored tokens inside form state after user click on authorize with webex cc button
     * also solves problem when form has initial values
     */
    if (!webexTokens) {
      return;
    }

    batch(() => {
      // also store time when authorization was successful
      change(TEMPORARY_FORM_FIELDS.startTime, webexTokens?.startTime);
      change(INTEGRATION_FORM_FIELDS.accessToken(integrationType), webexTokens.access_token);
      change(INTEGRATION_FORM_FIELDS.accessTokenExpires(integrationType), webexTokens.expires_in);
      change(INTEGRATION_FORM_FIELDS.refreshToken(integrationType), webexTokens.refresh_token);
      change(INTEGRATION_FORM_FIELDS.refreshTokenExpires(integrationType), webexTokens.refresh_token_expires_in);
      change(INTEGRATION_FORM_FIELDS.state, webexTokens.state);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webexTokens]);

  const handleWebexLogin = useCallback(() => {
    triggerWebexLogin(clientId, clientSecret, scopes);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, clientSecret]);

  const isLoginButtonDisabled = isInReadOnlyMode || isIdInvalid || isSecretInvalid || accessToken !== '';
  const isProgressActive =
    LOGIN_STATE.initiated === loginState ||
    LOGIN_STATE.inProgress === loginState ||
    LOGIN_STATE.retrievingTokens === loginState;

  return (
    <>
      {isProgressActive && <Progress withOverlay />}
      <Button
        onClick={handleWebexLogin}
        isDisabled={isLoginButtonDisabled}
        {...buttonProps}
        testId={TEST_IDS.buttonAuthorizeWithWebex}
      >
        <CheckmarkIcon />
        {t('myConfigurations.authorize')}
      </Button>
    </>
  );
};
